<script lang="ts">
  import { useThrelte } from '@threlte/core'
  import Camera from '../camera/Camera.svelte'
  import { interactivity } from '@threlte/extras'
  import { getColorFromTheme } from '../../helpers/theme'
  import {
    Box3,
    BoxGeometry,
    BufferAttribute,
    BufferGeometry,
    Color,
    LineSegments,
    Mesh,
    Object3D,
  } from 'three'
  import { setPreviewScene } from '../../stores/preview'
  import { getSceneLights } from '../../helpers/lights'
  import {
    gcodePreviewCurrentPointMaterial,
    gcodePreviewG0LineMaterial,
    gcodePreviewG1LineMaterial,
  } from '../../constants/three'
  import { PrinterService } from '../../services'
  import { focusOnBounds } from '../../stores/camera'

  type Props = {
    progress: number
    parsedGcode: ParsedGcodeLine[]
    displayG0: boolean
    printer?: {
      name: string
      color: string
      model: PrinterModel
    }
  }
  const { progress, parsedGcode, displayG0, printer }: Props = $props()
  const { scene, invalidate } = useThrelte()

  // Persistent scene objects. These are added to the scene once and never
  // rebuilt on progress changes — only the gcode draw range is updated.
  let printerMesh: Object3D | null | undefined
  let polarZone: Object3D | undefined
  const lights = getSceneLights()

  // The whole gcode is turned into two static LineSegments geometries (one for
  // G0 travel moves, one for G1 extrusion moves) that are built ONCE. Playback
  // then only reveals more of them via geometry.setDrawRange — no per-frame
  // allocation, no per-frame disposal, ~2 draw calls instead of thousands.
  type BuiltGeometry = {
    g0: LineSegments
    g1: LineSegments
    cube: Mesh
    // gcode line index of each segment / move, sorted ascending. Used to map a
    // `progress` (gcode line index) to how many vertices should be visible.
    g0Indices: Int32Array
    g1Indices: Int32Array
    moveIndices: Int32Array
    movePositions: Float32Array
  }
  let built = $state.raw<BuiltGeometry | null>(null)

  const buildGeometry = (gcode: ParsedGcodeLine[]): BuiltGeometry => {
    const g0Pos: number[] = []
    const g1Pos: number[] = []
    const g0Idx: number[] = []
    const g1Idx: number[] = []
    const moveIdx: number[] = []
    const movePos: number[] = []

    let prevX = 0
    let prevY = 0
    let prevZ = 0
    let hasPrev = false

    for (let i = 0; i < gcode.length; i++) {
      const line = gcode[i]

      if ((line.method === 'G0' || line.method === 'G1') && 'x' in line) {
        moveIdx.push(i)
        movePos.push(line.x, line.y, line.z)

        // Each move after the first draws a single segment from the previous
        // point to the current one, coloured by the current move's method.
        if (hasPrev) {
          if (line.method === 'G0') {
            g0Pos.push(prevX, prevY, prevZ, line.x, line.y, line.z)
            g0Idx.push(i)
          }
          else {
            g1Pos.push(prevX, prevY, prevZ, line.x, line.y, line.z)
            g1Idx.push(i)
          }
        }

        prevX = line.x
        prevY = line.y
        prevZ = line.z
        hasPrev = true
      }
    }

    const makeSegments = (
      positions: number[],
      material: LineSegments['material'],
    ) => {
      const geometry = new BufferGeometry()
      geometry.setAttribute(
        'position',
        new BufferAttribute(new Float32Array(positions), 3),
      )
      const segments = new LineSegments(geometry, material)
      // Bounds are computed from the full geometry; keep the partially-revealed
      // line from being frustum-culled while scrubbing.
      segments.frustumCulled = false
      return segments
    }

    const cube = new Mesh(
      new BoxGeometry(0.01, 0.01, 0.01),
      gcodePreviewCurrentPointMaterial,
    )
    cube.visible = false

    return {
      g0: makeSegments(g0Pos, gcodePreviewG0LineMaterial),
      g1: makeSegments(g1Pos, gcodePreviewG1LineMaterial),
      cube,
      g0Indices: Int32Array.from(g0Idx),
      g1Indices: Int32Array.from(g1Idx),
      moveIndices: Int32Array.from(moveIdx),
      movePositions: Float32Array.from(movePos),
    }
  }

  // Number of entries in a sorted index array whose value is <= progress.
  const countUpTo = (indices: Int32Array, upTo: number) => {
    let lo = 0
    let hi = indices.length
    while (lo < hi) {
      const mid = (lo + hi) >> 1
      if (indices[mid] <= upTo) lo = mid + 1
      else hi = mid
    }
    return lo
  }

  const disposeBuilt = (target: BuiltGeometry) => {
    scene.remove(target.g0, target.g1, target.cube)
    target.g0.geometry.dispose()
    target.g1.geometry.dispose()
    target.cube.geometry.dispose()
  // Materials are shared module-level constants and must NOT be disposed.
  }

  interactivity()

  $effect(() => {
    setPreviewScene(scene)
  })

  $effect(() => {
    scene.background = new Color(
      getColorFromTheme('--printer-pane-active-background'),
    )
  })

  // Static lights: added once, removed on unmount.
  $effect(() => {
    scene.add(lights)
    invalidate()
    return () => {
      scene.remove(lights)
    }
  })

  // Printer mesh + build zone: loaded asynchronously, added once, removed on
  // unmount or when the printer changes.
  $effect(() => {
    if (!printer) return
    let cancelled = false

    void (async () => {
      const printerDriver = PrinterService.getPrinterDriver(printer.model)
      const mesh = await printerDriver.getMesh(1, printer.color)
      const zone = printerDriver.hasPolarGrid
        ? await printerDriver.getPolarZone(printer.color)
        : await printerDriver.getCartesianZone(printer.color)

      if (cancelled) return
      printerMesh = mesh
      polarZone = zone
      scene.add(mesh, zone)
      invalidate()
    })()

    return () => {
      cancelled = true
      if (printerMesh) scene.remove(printerMesh)
      if (polarZone) scene.remove(polarZone)
      printerMesh = undefined
      polarZone = undefined
    }
  })

  // Build the gcode geometry ONCE per gcode (not per progress change).
  // This effect only reads `parsedGcode`; it writes `built` but never reads it,
  // so it cannot self-trigger. The previous geometry is disposed by the effect
  // cleanup (which captures `next`), running on rebuild and on unmount.
  $effect(() => {
    if (parsedGcode.length === 0) return

    const next = buildGeometry(parsedGcode)
    built = next
    scene.add(next.g0, next.g1, next.cube)
    invalidate()

    return () => {
      disposeBuilt(next)
    }
  })

  // Frame the camera on the toolpath itself rather than the printer mesh.
  // Re-runs if `printer` resolves after the gcode geometry is already built,
  // so the maxiprinter framing kicks in as soon as the model is known.
  $effect(() => {
    if (!built) return

    const box = new Box3().setFromObject(built.g0).union(new Box3().setFromObject(built.g1))
    if (box.isEmpty()) return

    focusOnBounds(box, printer?.model, false)
  })

  // Progressive reveal: the only work done on every progress / displayG0 change.
  $effect(() => {
    if (!built) return

    const g1Count = countUpTo(built.g1Indices, progress)
    built.g1.geometry.setDrawRange(0, g1Count * 2)
    built.g1.visible = g1Count > 0

    const g0Count = displayG0 ? countUpTo(built.g0Indices, progress) : 0
    built.g0.geometry.setDrawRange(0, g0Count * 2)
    built.g0.visible = g0Count > 0

    // Move the "current point" marker to the last revealed move.
    const moveCount = countUpTo(built.moveIndices, progress)
    if (moveCount > 0) {
      const offset = (moveCount - 1) * 3
      built.cube.position.set(
        built.movePositions[offset],
        built.movePositions[offset + 1],
        built.movePositions[offset + 2],
      )
      built.cube.visible = true
    }
    else {
      built.cube.visible = false
    }

    invalidate()
  })
</script>

<Camera />
